import { format } from 'date-fns';
import { Platform, StyleSheet } from 'react-native';
import { Box, Text, VStack } from '../../../components/primitives';
import { useHoverState } from '../../../hooks/useHoverState';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { CalendarEvent } from '../../../types/calendar';
import { type LocalDate, localDateKey, toNativeDate } from '../utils/calendarDate';
import { MonthEventChip } from './MonthEventChip';

interface CalendarMonthDayCellProps {
  date: LocalDate;
  /** Whether `date` belongs to the month being displayed, vs. a leading/trailing
   * day borrowed from the adjacent month to fill the grid. */
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
  maxVisibleEvents: number;
  /** Drills into Day view anchored on `date` — the cell already knows its
   * own date, so this takes no arguments. */
  onPress: () => void;
}

// Leading/trailing days stay visually quieter (text.tertiary, not bold) but
// never `text.disabled` — they remain real, tappable cells with real events,
// and `text.disabled` is reserved for genuinely inactive content elsewhere
// in this app.
export function CalendarMonthDayCell({
  date,
  isCurrentMonth,
  isToday,
  events,
  maxVisibleEvents,
  onPress,
}: Readonly<CalendarMonthDayCellProps>) {
  const visibleEvents = events.slice(0, maxVisibleEvents);
  const overflowCount = events.length - visibleEvents.length;

  const { isHovered, hoverProps } = useHoverState();

  const dateLabel = format(toNativeDate(date), 'EEEE, MMMM d, yyyy');
  const accessibilityLabel = [
    isToday ? 'Today' : null,
    dateLabel,
    events.length > 0 ? `${events.length} event${events.length === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Box
      testID={`calendar_month_day_${localDateKey(date)}`}
      pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      flex={1}
      minHeight={112}
      padding="xs"
      style={[
        styles.cell,
        // A month grid used to draw a full border on all 42 cells, which read
        // as a spreadsheet and broke the depth budget (canvas is never
        // bordered; one bordered surface per branch). Separation now comes
        // from a single hairline on two edges, so the grid reads as ruled
        // paper rather than a table of boxes.
        !isCurrentMonth && styles.outsideMonth,
        isToday && styles.today,
        isHovered && styles.hovered,
      ]}
      {...hoverProps}
    >
      <VStack spacing="xxs">
        {/* "Today" is never colour alone: a filled disc (shape + weight)
            around the day number, plus the "Today" prefix on the cell's
            accessibilityLabel above. The old outline badge was a 1px ring on
            a dark canvas — technically non-colour, practically invisible. */}
        {isToday ? (
          <Box style={styles.todayDisc} alignItems="center" justifyContent="center">
            <Text variant="label" color={colors.text.inverse} style={styles.todayNumber}>
              {date.day}
            </Text>
          </Box>
        ) : (
          <Text
            variant="label"
            color={isCurrentMonth ? colors.text.primary : colors.text.tertiary}
            style={{ fontWeight: isCurrentMonth ? '600' : '400' }}
          >
            {date.day}
          </Text>
        )}

        {visibleEvents.map((event) => (
          <MonthEventChip key={event.id} event={event} />
        ))}

        {overflowCount > 0 && (
          <Text variant="caption" color={colors.text.tertiary}>
            {`+${overflowCount} more`}
          </Text>
        )}
      </VStack>
    </Box>
  );
}

const styles = StyleSheet.create({
  cell: {
    overflow: 'hidden',
    minWidth: 0,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: colors.border.muted,
    ...Platform.select({
      web: {
        transitionProperty: 'background-color',
        transitionDuration: `${tokens.duration.fast}ms`,
        transitionTimingFunction: tokens.easing.default,
      },
      default: {},
    }),
  },
  // Recessed rather than faded: adjacent-month days stay legible and tappable,
  // they just sit behind the plane of the month being read.
  outsideMonth: {
    backgroundColor: colors.surface.bento.fill,
  },
  today: {
    backgroundColor: colors.primary.faint,
  },
  hovered: {
    backgroundColor: colors.surface.rowHover,
  },
  todayDisc: {
    width: 24,
    height: 24,
    borderRadius: tokens.radius.full,
    backgroundColor: colors.primary[500],
    alignSelf: 'flex-start',
  },
  todayNumber: {
    fontWeight: '700',
  },
});
